import * as React from 'react';
import classnames from 'classnames';
import { format, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Play, ZoomIn, ZoomOut } from 'lucide-react';
import { t } from '~/i18n';
import styles from './LocationTimeline.module.css';
import { TimelineWindow } from './useTimelineWindow';
import { TIMELINE_SPANS } from './timeline.utils';

type LocationTimelineControlsProps = {
  timeline: TimelineWindow;
  /** Names the window the timeline opened on, until it is zoomed or moved. */
  initialLabel?: string;
  /** A closed alarm has nothing live to follow. */
  allowLive?: boolean;
};

const spanLabel = (span: number) =>
  span < 3600000 ? `${Math.round(span / 60000)} min` : `${Math.round((span / 3600000) * 10) / 10} h`;

function rangeLabel(start: number, end: number) {
  if (isSameDay(start, end)) return `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
  return `${format(start, 'dd/MM HH:mm')} – ${format(end, 'dd/MM HH:mm')}`;
}

const LocationTimelineControls: React.FC<LocationTimelineControlsProps> = ({
  timeline,
  initialLabel,
  allowLive = true,
}) => {
  const { start, end, span, live } = timeline;

  const main = live ? t('locationTimeline.lastSpan', { span: spanLabel(span) }) : rangeLabel(start, end);
  const sub = live
    ? t('locationTimeline.untilNow', { start: format(start, isSameDay(start, end) ? 'HH:mm' : 'dd/MM HH:mm') })
    : timeline.isInitial && initialLabel
      ? initialLabel
      : format(end, 'dd/MM/yyyy');

  return (
    <div className={styles.Controls}>
      <div className={styles.WindowNavigation}>
        <button className={styles.IconButton} title={t('locationTimeline.earlier')} onClick={() => timeline.shift(-1)}>
          <ChevronLeft size={20} />
        </button>
        <button
          className={styles.IconButton}
          title={t('locationTimeline.zoomOut')}
          disabled={!timeline.canZoomOut}
          onClick={timeline.zoomOut}
        >
          <ZoomOut size={20} />
        </button>
        <div className={styles.WindowLabel}>
          <span className={styles.WindowMain}>{main}</span>
          <span className={styles.WindowSub}>{sub}</span>
        </div>
        <button
          className={styles.IconButton}
          title={t('locationTimeline.zoomIn')}
          disabled={!timeline.canZoomIn}
          onClick={timeline.zoomIn}
        >
          <ZoomIn size={20} />
        </button>
        <button
          className={styles.IconButton}
          title={t('locationTimeline.later')}
          disabled={!timeline.canShiftLater}
          onClick={() => timeline.shift(1)}
        >
          <ChevronRight size={20} />
        </button>
      </div>
      <div className={styles.WindowActions}>
        <div className={styles.Presets}>
          {TIMELINE_SPANS.map((preset) => (
            <button
              key={preset}
              className={classnames(styles.Preset, preset === span && styles.Active)}
              onClick={() => timeline.setSpan(preset)}
            >
              {spanLabel(preset).replace(' ', '')}
            </button>
          ))}
        </div>
        {allowLive && (
          <button className={classnames(styles.LiveButton, live && styles.Active)} onClick={timeline.goLive}>
            {live ? <span className={styles.LiveDot} /> : <Play size={14} />}
            {t('locationTimeline.live')}
          </button>
        )}
      </div>
    </div>
  );
};

export default LocationTimelineControls;
